import { Add, Delete, InfoOutlined } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import { FC, useMemo, useState } from "react";

import { AGDataGrid } from "../../../components/AGDataGrid";
import TableSearchInput from "../../../components/TableSearchInput";
import { useAppSelector } from "../../../redux";
import { useDeleteUserMutation, useGetUsersListQuery } from "../../../redux/reducers/userManagement.reducer";
import { IGetUsersListRequestData, PermissionDetailsType } from "../../../types";
import { ROWS_PER_PAGE } from "../../../utils/constants";
import "./index.css";
import UserDetailsDialog from "./UserDetailsDialog";
import { useGetUserRolesQuery } from "../../../redux/reducers/rolesPermissions.reducer";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { useNotification } from "../../../components/FeedbackNotification/useNotification";

export interface IUserRowDetails {
	id: number;
	sr_no: number;
	full_name: string;
	first_name: string;
	last_name: string;
	email: string;
	role: number;
	is_active: boolean;
	last_login: string;
	mobile_number: string;
	designation: string;
	validity_start_date: string;
	validity_end_date: string;
	permissions: PermissionDetailsType;
	rto?: number[];
	username: string;
	use_group_permissions_only: boolean;
	employee_id: string;
	challan_user_id: number;
	district_id: number;
}

const UsersList: FC = () => {
	const authState = useAppSelector((state) => state.auth);

	const { notify } = useNotification();

	const [openDialog, setOpenDialog] = useState(false);
	const [selectedUserDetails, setSelectedUserDetails] = useState<IUserRowDetails | null>(null);
	const [filters, setFilters] = useState<IGetUsersListRequestData>({ page: 1 });
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

	// GET USERS LIST
	const {
		data: getUsersListResponse,
		isLoading: getUsersListLoading,
		isFetching: getUsersListFetching,
		refetch: refetchUsersList
	} = useGetUsersListQuery(filters);

	// DELETE USER
	const [deleteUser, { isLoading: deleteUserLoading }] = useDeleteUserMutation();

	// GET USER ROLES
	const { data: getUserGroupsResponse } = useGetUserRolesQuery({});

	const readOnlyPermission = useMemo(
		() => !authState.user.permissions || !authState.user.permissions.user_management?.edit,
		[authState.user.permissions]
	);

	const userRolesMapping = useMemo<{ [id in number]: string }>(() => {
		if (!getUserGroupsResponse) return {};

		const mapping: { [id in number]: string } = {};

		for (const userGroup of getUserGroupsResponse) {
			mapping[userGroup.id] = userGroup.name;
		}

		return mapping;
	}, [getUserGroupsResponse]);

	const rowsData = useMemo<IUserRowDetails[]>(() => {
		if (!getUsersListResponse) return [];

		return getUsersListResponse.results.map((user, index) => ({
			id: user.id,
			sr_no: (filters.page - 1) * ROWS_PER_PAGE + index + 1,
			full_name: user.full_name,
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
			role: user.groups[0] ?? 0,
			is_active: user.is_active,
			last_login: user.last_login ?? "",
			mobile_number: user.phone_number,
			designation: user.designation ?? "",
			validity_start_date: user.validity_start_date ?? "",
			validity_end_date: user.validity_end_date ?? "",
			permissions: user.use_group_permissions_only ? user.groups_permissions : user.user_permissions,
			rto: user.jurisdiction,
			username: user.username,
			use_group_permissions_only: user.use_group_permissions_only,
			employee_id: user.employee_id ?? "",
			challan_user_id: user.challan_user_id ?? 0,
			district_id: user.district_id ?? 0
		}));
	}, [filters.page, getUsersListResponse]);

	function handleOpenDialog(userDetails: IUserRowDetails | null) {
		setOpenDialog(true);
		setSelectedUserDetails(userDetails);
	}

	function handleCloseDialog() {
		setOpenDialog(false);
		refetchUsersList();
	}

	function openDeleteConfirmationDialog(userDetails: IUserRowDetails) {
		setSelectedUserDetails(userDetails);
		setShowDeleteConfirmationDialog(true);
	}

	function closeDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
		setSelectedUserDetails(null);
	}

	function confirmDeleteUser() {
		if (!selectedUserDetails) return;

		deleteUser(selectedUserDetails.id)
			.unwrap()
			.then(() => {
				refetchUsersList();
				notify("User deleted successfully", "success");
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to delete user", "error");
			})
			.finally(() => {
				closeDeleteConfirmationDialog();
			});
	}

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 65,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "full_name",
			headerName: "Name",
			flex: 1,
			minWidth: 120,
			sortable: false
		},
		{
			field: "email",
			headerName: "Email",
			flex: 1,
			minWidth: 140,
			sortable: false
		},
		{
			field: "role",
			headerName: "Role",
			flex: 1,
			minWidth: 100,
			sortable: false,
			valueFormatter: (value: number) => userRolesMapping[value] ?? ""
		},
		{
			field: "is_active",
			headerName: "Status",
			flex: 1,
			minWidth: 100,
			sortable: false,
			valueFormatter: (value: boolean) => (value ? "Active" : "Inactive")
		},
		{
			field: "last_login",
			headerName: "Last Login",
			flex: 1,
			minWidth: 120,
			sortable: false,
			valueFormatter: (value: string) => (value ? moment.duration(moment(value).diff(moment())).humanize(true) : "N/A")
		},
		{
			field: "actions",
			headerName: "",
			width: readOnlyPermission ? 50 : 75,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					{!readOnlyPermission ? (
						<IconButton color="error" size="small" onClick={() => openDeleteConfirmationDialog(params.row)}>
							<Delete fontSize="inherit" />
						</IconButton>
					) : null}

					<IconButton color="info" size="small" onClick={() => handleOpenDialog(params.row)}>
						<InfoOutlined fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />

				{!readOnlyPermission ? (
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={() => handleOpenDialog(null)}
					>
						Add User
					</Button>
				) : null}
			</Box>

			<Box className="page-content">
				<AGDataGrid
					hideLastSeparator
					columns={columnsData}
					rows={rowsData}
					paginationMode="server"
					paginationModel={{ page: filters.page - 1, pageSize: ROWS_PER_PAGE }}
					onPaginationModelChange={handlePageChange}
					rowCount={getUsersListResponse?.count ?? 0}
					loading={getUsersListLoading || getUsersListFetching}
					hideFooter={(getUsersListResponse?.count ?? 0) <= ROWS_PER_PAGE}
				/>
			</Box>

			<UserDetailsDialog open={openDialog} userDetails={selectedUserDetails} onClose={handleCloseDialog} />

			<ConfirmationDialog
				width="xs"
				color="error"
				submitButtonText="Delete"
				title="Delete User"
				open={showDeleteConfirmationDialog}
				heading={`Are you sure you want to delete the user "${selectedUserDetails?.full_name ?? ""}"?`}
				onClose={closeDeleteConfirmationDialog}
				onConfirm={confirmDeleteUser}
				confirmButtonLoading={deleteUserLoading}
			/>
		</Box>
	);
};

export default UsersList;
